import { useState, useRef } from "react";
import { Upload, FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface DragDropUploadProps {
  onUpload: (file: File) => Promise<void>;
  isUploading: boolean;
  uploadedFile?: File | null;
  uploadSuccess?: boolean;
}

export const DragDropUpload = ({ onUpload, isUploading, uploadedFile, uploadSuccess = false }: DragDropUploadProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleFile = async (file: File) => {
    // Validate file type
    if (file.type !== 'application/pdf') {
      toast({
        title: "Invalid file type",
        description: "Please select a PDF file.",
        variant: "destructive",
      });
      return;
    }
    
    // Validate file size (100MB limit)
    if (file.size > 100 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "File size must be less than 100MB.",
        variant: "destructive",
      });
      return;
    }

    try {
      await onUpload(file);
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: "Failed to upload document. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div
      className={`
        w-full h-32 border-2 border-dashed rounded-lg 
        flex flex-col items-center justify-center
        cursor-pointer transition-colors
        ${isDragOver 
          ? 'border-blue-500 bg-blue-50' 
          : (uploadedFile && uploadSuccess)
            ? 'border-green-500 bg-green-50' 
            : 'border-gray-300 bg-gray-50 hover:border-gray-400'
        }
        ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
      `}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        onChange={handleFileChange}
        className="hidden"
      />
      
      {isUploading ? (
        <div className="flex flex-col items-center space-y-3">
          <div className="relative">
            <div className="w-8 h-8 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-blue-600">Uploading document...</p>
            <p className="text-xs text-gray-500">Please wait while we process your file</p>
          </div>
        </div>
      ) : (uploadedFile && uploadSuccess) ? (
        <div className="flex flex-col items-center space-y-2">
          <FileText className="w-8 h-8 text-green-600" />
          <div className="text-center">
            <p className="text-sm font-medium text-green-700">{uploadedFile.name}</p>
            <p className="text-xs text-green-600">File uploaded successfully</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center space-y-2">
          <Upload className={`w-8 h-8 ${isDragOver ? 'text-blue-500' : 'text-gray-400'}`} />
          <div className="text-center">
            <p className={`text-sm font-medium ${isDragOver ? 'text-blue-600' : 'text-gray-600'}`}>
              {isDragOver ? 'Drop your PDF here' : 'Click to upload or drag and drop'}
            </p>
            <p className="text-xs text-gray-500">PDF files only, up to 100MB</p>
          </div>
        </div>
      )}
    </div>
  );
};